import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "prix-site-internet-auto-entrepreneur",
  silo: "site-internet",
  isPillar: true,
  h1: "Combien coûte un site internet pour un auto-entrepreneur en 2026 ?",
  metaTitle: "Prix d'un site internet pour auto-entrepreneur en 2026",
  metaDescription: "Quel est le prix d'un site internet pour un auto-entrepreneur ou micro-entrepreneur ? Postes de coût, comparatif des solutions et coûts cachés à connaître avant de choisir.",
  excerpt: "Création, domaine, hébergement, maintenance : voici les vrais postes de coût d'un site internet pour auto-entrepreneur, et comment comparer les solutions sans mauvaise surprise.",
  publishedAt: "2026-10-09",
  readingMinutes: 7,
  coverImage: "/images/services/site-web.webp",
  coverImageAlt: "Création de site internet pour auto-entrepreneur et petite entreprise",
  relatedSlugs: ["wix-ou-professionnel-site-internet", "fiche-google-ou-site-internet", "informatique-tpe-pme-support-indispensable"],
  faq: [
    {
      question: "Quel est le prix d'un site internet pour un auto-entrepreneur ?",
      answer: "Cela dépend de l'approche : un outil à faire soi-même coûte surtout un abonnement mensuel et beaucoup de temps, alors qu'un freelance ou une agence se facturent souvent plusieurs centaines à plusieurs milliers d'euros. Les formules tout compris mensualisées répartissent le coût dans le temps.",
    },
    {
      question: "Faut-il payer chaque année pour un site internet ?",
      answer: "Oui, en général. Le nom de domaine se renouvelle chaque année et l'hébergement est récurrent. La maintenance et les mises à jour représentent aussi un coût, en argent ou en temps.",
    },
    {
      question: "Que comprend la formule SOS IT Services ?",
      answer: "La formule comprend la création d'un site jusqu'à 10 pages, la maintenance et les modifications mensuelles, pour 350 € à la mise en ligne puis 80 € par mois, avec un engagement de 12 mois. L'hébergement et le nom de domaine sont souscrits à votre nom, en plus.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Vous venez de lancer votre activité, ou vous voulez enfin donner une vitrine sérieuse à votre micro-entreprise, et la même question revient : combien coûte un site internet pour un auto-entrepreneur ? La réponse honnête est « ça dépend », mais ce n'est pas très utile. Dans cet article, nous détaillons les postes de coût, les différentes approches et les pièges à éviter, pour que vous puissiez décider en connaissance de cause.",
    },
    {
      type: "h2",
      text: "Les postes de coût d'un site internet",
    },
    {
      type: "p",
      text: "Le prix d'un site ne se résume pas à une facture de création. Pour un auto-entrepreneur ou un micro-entrepreneur, il faut raisonner sur la durée et regarder tous les postes.",
    },
    {
      type: "h3",
      text: "La création du site",
    },
    {
      type: "p",
      text: "C'est le poste le plus visible : conception des pages, rédaction ou mise en forme des textes, choix des images, adaptation au mobile, formulaire de contact. Selon que vous le faites vous-même ou que vous le confiez à un professionnel, ce coût est en temps ou en euros.",
    },
    {
      type: "h3",
      text: "Le nom de domaine",
    },
    {
      type: "p",
      text: "Votre adresse (par exemple monentreprise.fr) se loue, elle ne s'achète pas définitivement. Elle se renouvelle chaque année, pour un montant généralement modeste. Vérifiez toujours qu'elle est enregistrée à votre nom : c'est votre identité en ligne.",
    },
    {
      type: "h3",
      text: "L'hébergement",
    },
    {
      type: "p",
      text: "Pour qu'un site soit visible, il doit être hébergé sur un serveur. C'est un coût récurrent, parfois inclus dans un abonnement, parfois facturé à part. La qualité de l'hébergement influence la vitesse du site et sa disponibilité.",
    },
    {
      type: "h3",
      text: "La maintenance et les mises à jour",
    },
    {
      type: "p",
      text: "Un site doit être surveillé, sauvegardé et mis à jour, notamment pour des raisons de sécurité. C'est souvent le poste oublié. Sans maintenance, un site vieillit vite et devient plus vulnérable, un risque proche de ceux que nous décrivons dans notre guide sur les [virus et malwares](slug:virus-malware-ordinateur).",
    },
    {
      type: "h3",
      text: "Les modifications et l'évolution",
    },
    {
      type: "p",
      text: "Nouveau tarif, nouvelle prestation, changement de téléphone, ajout d'une page : votre activité évolue, et votre site doit suivre. Selon l'approche choisie, chaque modification est faite par vous, facturée à la demande, ou incluse dans un forfait.",
    },
    {
      type: "h2",
      text: "Comparer les approches",
    },
    {
      type: "h3",
      text: "Faire soi-même avec un outil de type Wix",
    },
    {
      type: "p",
      text: "Les créateurs de sites en ligne permettent de publier rapidement sans coder. Le coût principal est un abonnement mensuel, auquel s'ajoute votre temps de conception. C'est intéressant si vous aimez le sujet et disposez de quelques heures. Nous détaillons les avantages et limites dans notre comparatif [Wix ou un professionnel](slug:wix-ou-professionnel-site-internet).",
    },
    {
      type: "h3",
      text: "Faire appel à un freelance",
    },
    {
      type: "p",
      text: "Un indépendant peut concevoir un site sur mesure, avec un échange direct. Les tarifs varient fortement selon l'expérience et le projet, souvent de quelques centaines à plusieurs milliers d'euros. Pensez à demander qui s'occupera de l'hébergement et de la maintenance après la livraison.",
    },
    {
      type: "h3",
      text: "Passer par une agence",
    },
    {
      type: "p",
      text: "Une agence apporte une équipe (conception, rédaction, référencement) et une méthode. Cela se traduit généralement par un budget plus élevé, souvent plusieurs milliers d'euros, ce qui peut dépasser les moyens d'une micro-entreprise qui démarre.",
    },
    {
      type: "h3",
      text: "La formule tout compris mensualisée",
    },
    {
      type: "p",
      text: "Certains prestataires proposent un forfait qui regroupe création, hébergement et maintenance, avec un petit montant au départ puis une mensualité. L'avantage : un budget lisible et pas de démarches techniques. La contrepartie est souvent un engagement de durée, à lire attentivement avant de signer.",
    },
    {
      type: "h2",
      text: "Un site est-il vraiment utile pour une petite activité ?",
    },
    {
      type: "p",
      text: "C'est une question légitime quand on démarre. Pour beaucoup d'indépendants et d'artisans, le bouche-à-oreille reste le premier canal. Pourtant, un client recommandé vérifie presque toujours votre nom en ligne avant de vous appeler. Un site clair, avec vos services, votre zone d'intervention et un moyen de vous joindre, rassure et vous évite de répéter les mêmes explications. Il travaille pour vous, même lorsque vous êtes sur un chantier ou chez un client.",
    },
    {
      type: "h2",
      text: "Les coûts cachés à surveiller",
    },
    {
      type: "ul",
      items: [
        "Votre temps : des heures passées à construire le site sont des heures non facturées à vos clients.",
        "Les options payantes : certains outils facturent à part le nom de domaine, la suppression de la publicité ou des fonctions utiles.",
        "Le renouvellement : un tarif d'appel la première année peut augmenter ensuite.",
        "Les modifications facturées à chaque demande, parfois à un tarif horaire.",
        "La dépendance : si le site appartient au prestataire ou à la plateforme, le déplacer plus tard peut être compliqué.",
      ],
    },
    {
      type: "callout",
      variant: "warning",
      title: "À vérifier avant de signer",
      text: "Demandez toujours ce qui est inclus (hébergement, maintenance, modifications), la durée d'engagement, et ce que vous pouvez récupérer si vous arrêtez.",
    },
    {
      type: "h2",
      text: "L'offre SOS IT Services pour les indépendants",
    },
    {
      type: "p",
      text: "SOS IT Services, basé à Viry près de Genève et intervenant en Haute-Savoie, dans le Pays de Gex et dans le Genevois, propose une formule pensée pour les petites activités : 350 € à la mise en ligne, puis 80 € par mois, avec un engagement de 12 mois. Le site peut comporter jusqu'à 10 pages, et la maintenance ainsi que les modifications mensuelles sont incluses. L'hébergement et le nom de domaine restent à votre nom et à votre charge, pour quelques euros par mois : vous gardez ainsi la pleine propriété de votre site.",
    },
    {
      type: "p",
      text: "Concrètement, vous n'avez pas à gérer la technique : nous nous occupons de la mise en ligne et du suivi. Vous trouverez le détail sur notre page de [création de site internet pour auto-entrepreneur](page:/creation-site-web).",
    },
    {
      type: "h2",
      text: "Comment choisir sans se tromper ?",
    },
    {
      type: "ol",
      items: [
        "Définissez l'objectif : être trouvé localement, présenter vos services, recevoir des demandes de devis.",
        "Estimez le temps que vous pouvez réellement y consacrer.",
        "Comparez le coût sur 12 à 24 mois, tout compris, et pas seulement le prix de départ.",
        "Vérifiez qui gère l'hébergement, la maintenance et les modifications.",
        "Pensez à la visibilité : un site seul ne suffit pas, voir notre article sur la [fiche Google et le site internet](slug:fiche-google-ou-site-internet).",
      ],
    },
    {
      type: "p",
      text: "Si vous gérez déjà d'autres outils numériques dans votre activité, un partenaire unique facilite les choses, comme nous l'expliquons pour le [support informatique des TPE](slug:informatique-tpe-pme-support-indispensable).",
    },
    {
      type: "h2",
      text: "Conclusion",
    },
    {
      type: "p",
      text: "Le vrai prix d'un site internet, c'est la somme de la création, du domaine, de l'hébergement, de la maintenance et de votre temps. Pour un auto-entrepreneur ou un micro-entrepreneur, la meilleure option est celle qui reste lisible et tenable dans la durée. Si vous cherchez une solution clé en main (350 € à la mise en ligne, puis 80 € par mois, hébergement et maintenance inclus), découvrez notre offre de [création de site internet pour auto-entrepreneur](page:/creation-site-web).",
    },
  ],
};

export default article;
